import React from 'react';
import PropTypes from 'prop-types';
import { forceCollide } from 'd3-force';

// @see: https://github.com/vasturiano/react-force-graph/issues/30#issuecomment-407168131
import fromKapsule from 'react-kapsule';
// the default for force-graph was causing an error with out tests.
// importing the .js file fixes the issue
import ForceGraph2DKapsule from 'force-graph/dist/force-graph.js';
const ForceGraph2D = fromKapsule(
  ForceGraph2DKapsule,
  { methodNames: ['d3Force', 'd3ReheatSimulation', 'stopAnimation', 'centerAt', 'zoom', 'refresh'] }
);


class CategoryForceGraph extends React.Component {

  constructor(props) {
    super(props);
    this.setupRef = this.setupRef.bind(this);
    this.calculateCollideForce = this.calculateCollideForce.bind(this);
    this.forcesConfigured = false;
  }

  componentDidMount() {
    this.forcesConfigured = this.calculateCollideForce();

    /*
     * react-kapsule 2.x uses useImperativeHandle, which may not expose d3Force
     * immediately on mount. Schedule retry via requestAnimationFrame.
     */
    if (!this.forcesConfigured) {
      this.retryForceConfig();
    }
  }

  retryForceConfig() {
    if (this.forcesConfigured) {
      return;
    }
    requestAnimationFrame(() => {
      if (this.forcesConfigured) {
        return;
      }
      this.forcesConfigured = this.calculateCollideForce();
      if (!this.forcesConfigured) {
        this.retryForceConfig();
      }
    });
  }

  shouldComponentUpdate(nextProps) {
    const { nodes, width } = this.props;

    return (
      !this.forcesConfigured
      || nodes.length !== nextProps.nodes.length
      || width !== nextProps.width
      || this.props.sizeBy !== nextProps.sizeBy
    );
  }

  componentDidUpdate() {
    if (!this.forcesConfigured) {
      this.forcesConfigured = this.calculateCollideForce();
    }
  }

  componentWillUnmount() {
    if (this.graphRef?.stopAnimation) {
      this.graphRef.stopAnimation();
    }
  }

  setupRef(el) {
    this.graphRef = el;
  }

  refresh() {
    if (this.graphRef?.refresh) {
      this.graphRef.refresh();
    }
  }

  calculateCollideForce() {
    if (!this.graphRef?.d3Force) {
      return false;
    }

    const millisecondsToWaitForZoom = 1000;
    const normalZoomValue = 0.7;
    const negativeMultiplier = 0.02;
    const defaultMaxZoom = 0.2;
    const defaultNodeSize = 16;
    const nodeCollisionBuffer = 1;
    /*
     * react-kapsule 2.x may not expose props immediately. Use component props
     * as fallback when graphRef.props.graphData is unavailable.
     */
    const nodesLength = this.graphRef.props?.graphData?.nodes?.length ?? this.props.nodes.length;
    const value = Math.max(normalZoomValue - (negativeMultiplier * nodesLength), defaultMaxZoom);
    this.graphRef.zoom(value, millisecondsToWaitForZoom);
    this.graphRef.d3Force('charge').strength(1);
    const collideForce = forceCollide().radius((d) => (this.props.nodeVal(d) || defaultNodeSize) + nodeCollisionBuffer);
    this.graphRef.d3Force('collide', collideForce);

    /*
     * react-kapsule 2.x uses useImperativeHandle, which may not expose d3Force
     * immediately on mount. If forces are configured late (during componentDidUpdate),
     * reheat the simulation to restart with the correct force parameters.
     */
    if (this.graphRef.d3ReheatSimulation) {
      this.graphRef.d3ReheatSimulation();
    }
    return true;
  }

  render() {
    const {
      nodes,
      width,
      nodeCanvasObject,
      nodeVal,
      onNodeHover
    } = this.props;

    return (
      <ForceGraph2D
        width={width}
        height={175}
        enableNodeDrag={false}
        autoPauseRedraw={false}
        ref={this.setupRef}
        graphData={{ nodes, links: [] }}
        nodeCanvasObject={nodeCanvasObject}
        nodeVal={nodeVal}
        onNodeHover={onNodeHover}
      />
    );
  }
}

CategoryForceGraph.propTypes = {
  nodes: PropTypes.array.isRequired,
  width: PropTypes.number.isRequired,
  nodeCanvasObject: PropTypes.func.isRequired,
  nodeVal: PropTypes.func.isRequired,
  onNodeHover: PropTypes.func.isRequired,
  sizeBy: PropTypes.string.isRequired
};

CategoryForceGraph.defaultProps = {
  nodes: []
};

export default CategoryForceGraph;
